import React, { useMemo, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Plus, Trash2, FileText, Layers } from "lucide-react";

/**
 * Editor for the `TestLayoutParameters` JSON column.
 *
 * Verified shape (triangles / rvlh):
 *   {
 *     categoryTestInstruction: "<html>",          // rich HTML string
 *     "<N>": {                                    // numbered paper/language variant
 *       instructions: {
 *         header: "<html>",
 *         parentInstructions: "<html>",
 *         childInstructions: [ { "<sectionId>": "<html>" }, ... ]
 *       }
 *     }
 *   }
 *
 * Unknown/unrecognised keys are preserved untouched on every write.
 */

const EDITOR_CONFIG = {
  toolbar: [
    "heading",
    "|",
    "bold",
    "italic",
    "underline",
    "link",
    "bulletedList",
    "numberedList",
    "|",
    "outdent",
    "indent",
    "|",
    "insertTable",
    "blockQuote",
    "undo",
    "redo",
    "sourceEditing",
  ],
};

interface RichFieldProps {
  label: string;
  value: string;
  onChange: (html: string) => void;
  hint?: string;
}

const RichField: React.FC<RichFieldProps> = ({
  label,
  value,
  onChange,
  hint,
}) => (
  <div className="tcs-field">
    <label className="tcs-label">{label}</label>
    {hint && <p className="tcs-hint">{hint}</p>}
    <CKEditor
      editor={ClassicEditor as any}
      config={EDITOR_CONFIG as any}
      data={value || ""}
      onChange={(_evt: any, editor: any) => onChange(editor.getData())}
    />
  </div>
);

const isVariantKey = (key: string) => /^\d+$/.test(key);

interface Props {
  /** Parsed TestLayoutParameters object */
  value: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
}

const LayoutParamsEditor: React.FC<Props> = ({ value, onChange }) => {
  const tlp = value || {};

  const variantKeys = useMemo(
    () => Object.keys(tlp).filter(isVariantKey).sort((a, b) => Number(a) - Number(b)),
    [tlp],
  );

  const otherKeys = useMemo(
    () =>
      Object.keys(tlp).filter(
        (k) => !isVariantKey(k) && k !== "categoryTestInstruction",
      ),
    [tlp],
  );

  const [newVariantKey, setNewVariantKey] = useState("");
  const [variantError, setVariantError] = useState("");

  // -- writers (always spread-preserve unknown keys) ----------------------

  const setCategoryInstruction = (html: string) =>
    onChange({ ...tlp, categoryTestInstruction: html });

  const getInstructions = (key: string) => tlp[key]?.instructions || {};

  const setInstructionField = (key: string, field: string, val: any) => {
    const variant = tlp[key] || {};
    onChange({
      ...tlp,
      [key]: {
        ...variant,
        instructions: { ...(variant.instructions || {}), [field]: val },
      },
    });
  };

  const setChildInstruction = (
    variantKey: string,
    index: number,
    sectionKey: string,
    html: string,
  ) => {
    const children = [...(getInstructions(variantKey).childInstructions || [])];
    children[index] = { [sectionKey]: html };
    setInstructionField(variantKey, "childInstructions", children);
  };

  const addChildInstruction = (variantKey: string) => {
    const children = [...(getInstructions(variantKey).childInstructions || [])];
    // next numeric section key
    const used = children
      .map((c: any) => Number(Object.keys(c || {})[0]))
      .filter((n) => !Number.isNaN(n));
    const next = used.length ? Math.max(...used) + 1 : 1;
    children.push({ [String(next)]: "" });
    setInstructionField(variantKey, "childInstructions", children);
  };

  const removeChildInstruction = (variantKey: string, index: number) => {
    const children = [...(getInstructions(variantKey).childInstructions || [])];
    children.splice(index, 1);
    setInstructionField(variantKey, "childInstructions", children);
  };

  const renameChildKey = (
    variantKey: string,
    index: number,
    nextKey: string,
  ) => {
    const children = [...(getInstructions(variantKey).childInstructions || [])];
    const current = children[index] || {};
    const oldKey = Object.keys(current)[0] ?? "";
    children[index] = { [nextKey]: current[oldKey] ?? "" };
    setInstructionField(variantKey, "childInstructions", children);
  };

  const addVariant = () => {
    const key = newVariantKey.trim();
    if (!key) {
      setVariantError("Enter a variant number");
      return;
    }
    if (!isVariantKey(key)) {
      setVariantError("Variant key must be numeric");
      return;
    }
    if (tlp[key]) {
      setVariantError(`Variant ${key} already exists`);
      return;
    }
    setVariantError("");
    setNewVariantKey("");
    onChange({
      ...tlp,
      [key]: {
        instructions: {
          header: "",
          parentInstructions: "",
          childInstructions: [],
        },
      },
    });
  };

  const removeVariant = (key: string) => {
    const next = { ...tlp };
    delete next[key];
    onChange(next);
  };

  // -- render -------------------------------------------------------------

  return (
    <div>
      {/* Category-level instruction */}
      <div className="tcs-card" style={{ padding: 16, marginBottom: 16 }}>
        <div className="tcs-variant-key" style={{ marginBottom: 10 }}>
          <FileText size={15} /> Category Test Instruction
        </div>
        <RichField
          label=""
          hint="Shown to students before the test starts (applies to the whole category)."
          value={tlp.categoryTestInstruction || ""}
          onChange={setCategoryInstruction}
        />
      </div>

      {/* Paper / language variants */}
      <div className="tcs-variant-head">
        <div className="tcs-variant-key">
          <Layers size={15} /> Paper Variants ({variantKeys.length})
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="tcs-input tcs-input-sm"
            style={{ width: 130 }}
            placeholder="Variant no."
            value={newVariantKey}
            onChange={(e) => {
              setNewVariantKey(e.target.value);
              setVariantError("");
            }}
          />
          <button
            type="button"
            className="tcs-btn tcs-btn-secondary tcs-btn-sm"
            onClick={addVariant}
          >
            <Plus size={14} /> Add Variant
          </button>
        </div>
      </div>
      {variantError && (
        <div className="tcs-alert tcs-alert-danger">{variantError}</div>
      )}

      {variantKeys.length === 0 && (
        <p className="tcs-hint">
          No paper variants defined. Variants hold per-paper instruction
          layouts (header, parent instructions, and per-section child
          instructions).
        </p>
      )}

      {variantKeys.map((key) => {
        const instr = getInstructions(key);
        const children: any[] = instr.childInstructions || [];
        return (
          <div key={key} className="tcs-variant">
            <div className="tcs-variant-head">
              <span className="tcs-variant-key">
                <Layers size={14} /> Variant {key}
              </span>
              <button
                type="button"
                className="tcs-btn tcs-btn-danger tcs-btn-sm"
                onClick={() => removeVariant(key)}
              >
                <Trash2 size={13} /> Remove
              </button>
            </div>

            <RichField
              label="Header"
              hint="Rendered at the top of the printed / on-screen paper."
              value={instr.header || ""}
              onChange={(html) => setInstructionField(key, "header", html)}
            />

            <RichField
              label="Parent Instructions"
              value={instr.parentInstructions || ""}
              onChange={(html) =>
                setInstructionField(key, "parentInstructions", html)
              }
            />

            <div className="tcs-field">
              <div className="tcs-variant-head">
                <label className="tcs-label" style={{ marginBottom: 0 }}>
                  Child Instructions ({children.length})
                </label>
                <button
                  type="button"
                  className="tcs-btn tcs-btn-secondary tcs-btn-sm"
                  onClick={() => addChildInstruction(key)}
                >
                  <Plus size={13} /> Add Section
                </button>
              </div>
              <p className="tcs-hint">
                One entry per test section — the key is the section number.
              </p>

              {children.map((child, idx) => {
                const sectionKey = Object.keys(child || {})[0] ?? "";
                const html = child?.[sectionKey] ?? "";
                return (
                  <div
                    key={idx}
                    className="tcs-card"
                    style={{ padding: 12, marginBottom: 10 }}
                  >
                    <div className="tcs-variant-head">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span className="tcs-label" style={{ marginBottom: 0 }}>
                          Section
                        </span>
                        <input
                          className="tcs-input"
                          style={{ width: 90 }}
                          value={sectionKey}
                          onChange={(e) =>
                            renameChildKey(key, idx, e.target.value)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="tcs-btn tcs-btn-danger tcs-btn-sm"
                        onClick={() => removeChildInstruction(key, idx)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <CKEditor
                      editor={ClassicEditor as any}
                      config={EDITOR_CONFIG as any}
                      data={html}
                      onChange={(_e: any, editor: any) =>
                        setChildInstruction(
                          key,
                          idx,
                          sectionKey,
                          editor.getData(),
                        )
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {otherKeys.length > 0 && (
        <p className="tcs-hint" style={{ marginTop: 12 }}>
          Note: {otherKeys.length} additional key(s) present (
          {otherKeys.join(", ")}) are preserved as-is — edit them in the Raw
          JSON tab.
        </p>
      )}
    </div>
  );
};

export default LayoutParamsEditor;
